import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core'
import { Observable } from 'rxjs'
import { OrdersFirestoreService } from 'src/app/core/firestore-services/orders-firestore.service'
import { ShoeOrderItem } from 'domain/order.model'
import { ShopSettingsService } from 'src/app/modules/auth/shop-settings.service'
import { FileUploadService } from 'src/app/shared/services/file-upload.service'
import { PixelService } from '@bloomscorp/ngx-pixel'
import { TranslateService } from '@ngx-translate/core'
import { assertNonNullish } from '../../../../../../commons/assertions'
import { CartOrder } from './cart-order.type'
import { toObservable } from '@angular/core/rxjs-interop'

@Injectable({
	providedIn: 'root',
})
export class CartService {
	private readonly ordersFirestoreService = inject(OrdersFirestoreService)
	private uploadService = inject(FileUploadService)
	private readonly shopSettingsService = inject(ShopSettingsService)
	private readonly pixel = inject(PixelService)
	private readonly translateService = inject(TranslateService)

	private readonly shopUrl: Signal<string | undefined> = computed(() => this.shopSettingsService.shopSettings()?.url)

	private readonly cartKey: Signal<string | undefined> = computed(() => {
		const shopUrl = this.shopUrl()
		if (!shopUrl) {
			return undefined
		}
		return `cys-cart-${shopUrl}`
	})

	private readonly currentCartOrder = signal<CartOrder | null>(null)

	readonly currentOrder = computed(() => this.currentCartOrder())
	readonly currentOrder$: Observable<CartOrder | null> = toObservable(this.currentOrder)

	readonly currentOrderItems: Signal<Array<ShoeOrderItem>> = computed(() => this.currentCartOrder()?.items ?? [])

	readonly totalItems: Signal<number> = computed(() => this.currentOrderItems().length)

	readonly hasItems: Signal<boolean> = computed(() => this.totalItems() > 0)

	readonly totalItemsAmount: Signal<number> = computed(
		() =>
			this.currentOrderItems()
				.map((item) => item.amount)
				.reduce((amount, total) => amount + total, 0) ?? 0,
	)

	readonly totalBuyerPrice: Signal<number> = computed(
		() =>
			this.currentOrderItems()
				.map((item) => item.amount * item.buyerPrice)
				.reduce((amount, total) => amount + total, 0) ?? 0,
	)

	readonly totalAdminPrice: Signal<number> = computed(
		() =>
			this.currentOrderItems()
				.map((item) => item.amount * item.adminPrice)
				.reduce((amount, total) => amount + total, 0) ?? 0,
	)

	constructor() {
		effect(() => {
			const cartKey = this.cartKey()
			const currentCartOrder = this.currentCartOrder()

			if (cartKey && !currentCartOrder) {
				this.initializeCart(cartKey)
			}
		})

		effect(() => {
			const cartKey = this.cartKey()
			const order = this.currentCartOrder()
			if (cartKey && order) {
				this.persistCartChanges(cartKey, order)
			}
		})
	}

	clearCart() {
		this.currentCartOrder.set(this.createNewCartOrder())
	}

	addItem(shoeOrderItem: ShoeOrderItem) {
		this.currentCartOrder.update((order) => {
			assertNonNullish(order)
			return { ...order, items: [...order.items, shoeOrderItem] }
		})

		try {
			this.translateService.get(shoeOrderItem.shoeDisplayName).subscribe((shoeName) =>
				this.pixel.track('AddToCart', {
					content_name: shoeName,
					content_type: 'product',
				}),
			)
		} catch (err) {
			console.error(err)
		}
	}

	async removeItem(itemId: string) {
		const cartOrder = this.currentCartOrder()
		assertNonNullish(cartOrder, 'Cart order is null or undefined')

		const item = cartOrder.items.find((shoeOrder) => shoeOrder.id == itemId)
		assertNonNullish(item, 'Item is null or undefined')

		await this.uploadService.deleteFile(item.screenshots.position1StoragePath)
		await this.uploadService.deleteFile(item.screenshots.position2StoragePath)
		await this.uploadService.deleteFile(item.screenshots.position3StoragePath)

		this.currentCartOrder.update((order) => {
			assertNonNullish(order)
			return { ...order, items: [...order.items.filter((shoeOrder) => shoeOrder.id !== itemId)] }
		})
	}

	setItemAmount(itemId: string, amount: number) {
		this.currentCartOrder.update((order) => {
			assertNonNullish(order)
			const itemIndex = order.items.findIndex((shoeOrder) => shoeOrder.id == itemId)
			if (itemIndex !== -1) {
				const updatedItems = [...order.items]
				updatedItems[itemIndex].amount = amount
				return { ...order, items: updatedItems }
			}
			return order
		})
	}

	private initializeCart(storageKey: string) {
		const storedOrderString = localStorage.getItem(storageKey)
		const cartOrder: CartOrder = storedOrderString ? JSON.parse(storedOrderString) : this.createNewCartOrder()
		this.currentCartOrder.set(cartOrder)
	}

	private createNewCartOrder(): CartOrder {
		return {
			uid: this.ordersFirestoreService.generateUid(),
			items: [],
			customer: '',
			address: '',
			city: '',
			county: '',
			email: '',
			phone: '',
		}
	}

	private persistCartChanges(storageKey: string, order: CartOrder) {
		localStorage.setItem(storageKey, JSON.stringify(order))
	}
}
